import { isContentEmpty } from '@/shared/tools/htmlHelper'
import { HomeMessage, HomeMessageSection, SectionView } from './types'

export const COLUMNS = 3

const escapeHtml = (text: string) => text.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;')

const isElement = (node: Node): node is Element => node.nodeType === Node.ELEMENT_NODE

const isText = (node: Node) => node.nodeType === Node.TEXT_NODE

const isTitle = (node: Node) => isElement(node) && node.tagName === 'H3'

const wrapsTitle = (node: Node) => isElement(node) && node.querySelector('h3') !== null

const slugify = (title: string) =>
  title
    .normalize('NFD')
    .replace(/\p{M}/gu, '')
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '')

// Ids come from the titles, not the positions, so a board update that inserts a card keeps the same card open.
const idFactory = (section: HomeMessageSection) => {
  const seen = new Map<string, number>()

  return (title: string) => {
    const slug = slugify(title) || 'untitled'
    const times = (seen.get(slug) ?? 0) + 1
    seen.set(slug, times)
    return times === 1 ? `${section}-${slug}` : `${section}-${slug}-${times}`
  }
}

// One HTML document per section, written in Dev Tools: every "Heading 3" starts a card, even inside a pasted wrapper.
export const splitBoardSection = (html: string, section: HomeMessageSection): HomeMessage[] => {
  const messages: HomeMessage[] = []
  const idFor = idFactory(section)
  let title = ''
  let parts: string[] = []
  let started = false

  const flush = () => {
    const content = parts.join('')
    if (title !== '' || !isContentEmpty(content)) {
      messages.push({ id: idFor(title), section, title, content })
    }
  }

  const visit = (node: Node) => {
    if (isTitle(node)) {
      if (started) flush()
      started = true
      title = (node.textContent ?? '').trim()
      parts = []
      return
    }
    if (wrapsTitle(node)) {
      node.childNodes.forEach(visit)
      return
    }
    if (!isElement(node) && !isText(node)) return
    started = true
    parts.push(isElement(node) ? node.outerHTML : escapeHtml(node.textContent ?? ''))
  }

  new DOMParser().parseFromString(html, 'text/html').body.childNodes.forEach(visit)
  if (started) flush()

  return messages
}

const stripHtml = (html: string) => html.replace(/<[^>]+>/g, ' ')

const normalize = (text: string) => text.normalize('NFD').replace(/\p{M}/gu, '').toLowerCase()

export const filterMessages = (messages: HomeMessage[], search: string): HomeMessage[] => {
  const needle = normalize(search.trim())
  if (needle === '') return messages

  return messages.filter(
    message => normalize(message.title).includes(needle) || normalize(stripHtml(message.content)).includes(needle),
  )
}

export const chunk = <T>(items: T[], size: number): T[][] => {
  const rows: T[][] = []
  for (let index = 0; index < items.length; index += size) {
    rows.push(items.slice(index, index + size))
  }
  return rows
}

export const buildSectionView = (messages: HomeMessage[], search: string, expandedId: string | null): SectionView => {
  const visible = filterMessages(messages, search)
  const isSearching = search.trim() !== ''

  return {
    messages,
    rows: chunk(visible, COLUMNS).map(row => ({
      key: row[0].id,
      messages: row,
      expanded: row.find(message => message.id === expandedId) ?? null,
    })),
    count: isSearching ? `${visible.length} / ${messages.length}` : String(messages.length),
    isEmpty: messages.length === 0,
    hasNoResults: visible.length === 0,
  }
}
